import React from 'react';
import { useReactPWAInstall } from 'react-pwa-install';

import Button from '@material-ui/core/Button';
import CloseIcon from '@material-ui/icons/Close';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import FullScreenDialog, { FullScreenDialogProps } from '../FullScreenDialog';
import useCompanyContext from '../../hooks/useCompanyContext';

import Add2Home from '../../assets/images/addToHome.png';
import CompanionLogo from '../../assets/images/companion-logo.png';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: theme.spacing(6),
  },
  mainContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  merchantLogo: {
    height: 120,
    width: 120,
    borderRadius: '50%',
    objectFit: 'cover',
    boxShadow: '0 2px 10px rgba(199, 199, 199, 0.5)',
  },
  header: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(5),
    textAlign: 'center',
    fontWeight: theme.typography.fontWeightRegular,
  },
  box: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  close: {
    marginRight: 'auto',
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
  actionContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: theme.spacing(5),
  },
  logoContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    textAlign: 'center',
    fontWeight: theme.typography.fontWeightRegular,
  },
  text: {
    marginBottom: theme.spacing(2),
  },
  actionButton: {
    width: 200,
  },
  addToHomeContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 50,
    marginBottom: 50,
  },
  addtoHome: {
    width: 325,
    height: 130,
  },
}));

type Props = Pick<FullScreenDialogProps, 'open'> & {
  onClose: () => void;
};

export default function SaveToHomeScreenDialog({ open, onClose }: Props) {
  const classes = useStyles();
  const { company } = useCompanyContext();
  const { pwaInstall, supported, isInstalled } = useReactPWAInstall();

  const handleClick = () => {
    pwaInstall({
      title: 'Save Companion App',
      logo: CompanionLogo,
    })
      .then(() => alert('Companion App installed successfully'))
      .catch(() => alert('User opted out from installing'))
      .finally(() => onClose());
  };

  if (!company) {
    return null;
  }

  return (
    <FullScreenDialog open={open}>
      <div className={classes.box}>
        <div className={classes.close}>
          <IconButton onClick={onClose}>
            <CloseIcon className={classes.closeIcon} />
          </IconButton>
        </div>
      </div>
      <div className={classes.logoContainer}>
        <img
          src={company.logo?.versions.xl}
          className={classes.merchantLogo}
          alt="flex reward"
        />
        <Typography variant="h4" className={classes.header}>
          SAVE TO HOMESCREEN
        </Typography>
        <Grid item xs={8}>
          <Typography variant="h6" className={classes.title} gutterBottom>
            Please save the Companion App to your homescreen.
          </Typography>
          <Typography variant="h6" className={classes.title} gutterBottom>
            This makes it quicker to launch next time.
          </Typography>
          <div className={classes.addToHomeContainer}>
            <img
              src={Add2Home}
              className={classes.addtoHome}
              alt="add to home"
            />
          </div>
          {supported() && !isInstalled() && (
            <div className={classes.actionContainer}>
              <Button
                color="primary"
                size="large"
                className={classes.actionButton}
                onClick={handleClick}
              >
                Save to Homescreen
              </Button>
            </div>
          )}
        </Grid>
      </div>
    </FullScreenDialog>
  );
}
